import React, { useEffect, useState, useRef } from "react";
import "./sales-table.scss";
import { post } from "../../services/smartApiService";
import { MIS_REPORT_URLS } from "../../api/UserUrls";
import { roundAmount } from "../../services/core/CommonService";
import { format } from "date-fns";
import * as ExcelJS from "exceljs";
import { SmartAlert } from "soft_digi";

interface headerProps {
  month: number;
  year: any;
}

interface SalesData {
  state_name?: string;
  taxable_value: number;
  igst_amount: number;
  cgst_amount: number;
  sgst_amount: number;
  invoice_value: number;
}



const SalesDetailsTable: React.FC<headerProps> = ({ year, month }) => {
  const [data, setData] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [exporting, setExporting] = useState<boolean>(false);
  const printRef = useRef<HTMLDivElement>(null);

  // Helper function to calculate totals
  const calculateTotal = (
    dataArray: SalesData[] | undefined,
    key: keyof SalesData
  ): number => {
    if (!dataArray || !Array.isArray(dataArray)) return 0;
    return dataArray.reduce((acc: any, curr) => acc + (curr[key] || 0), 0);
  };

  // Memoized totals calculations
  const totals = {
    taxable: calculateTotal(data?.sales_details, "taxable_value"),
    igst: calculateTotal(data?.sales_details, "igst_amount"),
    cgst: calculateTotal(data?.sales_details, "cgst_amount"),
    sgst: calculateTotal(data?.sales_details, "sgst_amount"),
    invoice: calculateTotal(data?.sales_details, "invoice_value"),
  };

  const creditTotals = {
    taxable: calculateTotal(data?.credit_notes, "taxable_value"),
    igst: calculateTotal(data?.credit_notes, "igst_amount"),
    cgst: calculateTotal(data?.credit_notes, "cgst_amount"),
    sgst: calculateTotal(data?.credit_notes, "sgst_amount"),
    invoice: calculateTotal(data?.credit_notes, "invoice_value"),
  };

  const finalTotals = {
    taxable: calculateTotal(data?.final_sales, "taxable_value"),
    igst: calculateTotal(data?.final_sales, "igst_amount"),
    cgst: calculateTotal(data?.final_sales, "cgst_amount"),
    sgst: calculateTotal(data?.final_sales, "sgst_amount"),
    invoice: calculateTotal(data?.final_sales, "invoice_value"),
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const _data = { year, month };
      const URL = MIS_REPORT_URLS.GET_SALES_REPORT;
      const response = await post(URL, _data).toPromise();
      if (response && response.data) {
        setData(response.data);
      } else {
        throw new Error("No data received");
      }
    } catch (error) {
      console.error("Error loading data:", error);
      SmartAlert.show({
        title: "Load Failed",
        content: "Failed to load sales data. Please try again.",
        type: "danger",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [year, month]);

  const date = new Date(year, month - 1, 1);
  const formatted = format(date, "MMM-yyyy");

  const handlePrint = () => {
    const printWindow = window.open("", "_blank", "width=800,height=600");
    if (printWindow && printRef.current) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Sales Report - ${formatted}</title>
            <style>
              body { font-family: Arial; margin: 20px; }
              .table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
              .table th, .table td { border: 1px solid #ddd; padding: 8px; text-align: center; }
              .has-background-warning { background-color: #ffdd57 !important; }
              .title.is-4 { font-size: 1.5rem; margin-bottom: 1rem; }
              .mt-4 { margin-top: 1.5rem !important; }
              .mt-3 { margin-top: 1rem !important; }
              .no-print { display: none !important; }
              @page { size: auto; margin: 5mm; }
            </style>
          </head>
          <body>
            <h2 style="text-align: center;">Sales Report - ${formatted}</h2>
            ${printRef.current.innerHTML}
            <script>
              setTimeout(() => {
                window.print();
                window.close();
              }, 200);
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      if (!data) {
        SmartAlert.show({
          title: "No Data",
          content: "There is no data to export.",
          type: "warning",
        });
        return;
      }

      const fileName = `Sales_Report_${formatted.replace(/ /g, "_")}.xlsx`;
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Sales Report System";
      workbook.created = new Date();

      // Define styles
      const titleStyle: Partial<ExcelJS.Style> = {
        font: { bold: true, size: 14 },
        alignment: { horizontal: "center" as const },
        fill: {
          type: "pattern" as const,
          pattern: "solid" as const,
          fgColor: { argb: "FFFF00" },
        },
      };

      const headerStyle: Partial<ExcelJS.Style> = {
        font: { bold: true, color: { argb: "000000" } },
        fill: {
          type: "pattern" as const,
          pattern: "solid" as const,
          fgColor: { argb: "D3D3D3" },
        },
        border: {
          top: { style: "thin" as const },
          left: { style: "thin" as const },
          bottom: { style: "thin" as const },
          right: { style: "thin" as const },
        },
      };

      const totalStyle: Partial<ExcelJS.Style> = {
        font: { bold: true },
        fill: {
          type: "pattern" as const,
          pattern: "solid" as const,
          fgColor: { argb: "FFFF00" },
        },
      };

      // Add Sales Details sheet if data exists
      if (data.sales_details && data.sales_details.length > 0) {
        const salesSheet = workbook.addWorksheet("Sales Details");
        salesSheet.mergeCells("A1:F1");
        salesSheet.getCell("A1").value = `Sales Details - ${formatted}`;
        salesSheet.getCell("A1").style = titleStyle;

        salesSheet
          .addRow([
            "State",
            "Taxable Value",
            "IGST Amount",
            "CGST Amount",
            "SGST/UTGST Amount",
            "Invoice Value",
          ])
          .eachCell((cell) => {
            cell.style = headerStyle;
          });

        data.sales_details.forEach((row: SalesData) => {
          salesSheet.addRow([
            row.state_name || "-",
            roundAmount(row.taxable_value),
            roundAmount(row.igst_amount),
            roundAmount(row.cgst_amount),
            roundAmount(row.sgst_amount),
            roundAmount(row.invoice_value),
          ]);
        });

        salesSheet
          .addRow([
            "Grand Total",
            roundAmount(totals.taxable),
            roundAmount(totals.igst),
            roundAmount(totals.cgst),
            roundAmount(totals.sgst),
            roundAmount(totals.invoice),
          ])
          .eachCell((cell) => {
            cell.style = totalStyle;
          });

        salesSheet.columns = [
          { key: "state", width: 20 },
          { key: "taxable", width: 15 },
          { key: "igst", width: 15 },
          { key: "cgst", width: 15 },
          { key: "sgst", width: 15 },
          { key: "invoice", width: 15 },
        ];
      }

      // Add Credit Notes sheet if data exists
      if (data.credit_notes && data.credit_notes.length > 0) {
        const creditSheet = workbook.addWorksheet("Credit Notes");
        creditSheet.mergeCells("A1:F1");
        creditSheet.getCell("A1").value = `Credit Notes - ${formatted}`;
        creditSheet.getCell("A1").style = titleStyle;

        creditSheet
          .addRow([
            "State",
            "Taxable Value",
            "IGST Amount",
            "CGST Amount",
            "SGST/UTGST Amount",
            "Invoice Value",
          ])
          .eachCell((cell) => {
            cell.style = headerStyle;
          });

        data.credit_notes.forEach((row: SalesData) => {
          creditSheet.addRow([
            row.state_name || "-",
            roundAmount(row.taxable_value),
            roundAmount(row.igst_amount),
            roundAmount(row.cgst_amount),
            roundAmount(row.sgst_amount),
            roundAmount(row.invoice_value),
          ]);
        });

        creditSheet
          .addRow([
            "Grand Total",
            roundAmount(creditTotals.taxable),
            roundAmount(creditTotals.igst),
            roundAmount(creditTotals.cgst),
            roundAmount(creditTotals.sgst),
            roundAmount(creditTotals.invoice),
          ])
          .eachCell((cell) => {
            cell.style = totalStyle;
          });

        creditSheet.columns = [
          { key: "state", width: 20 },
          { key: "taxable", width: 15 },
          { key: "igst", width: 15 },
          { key: "cgst", width: 15 },
          { key: "sgst", width: 15 },
          { key: "invoice", width: 15 },
        ];
      }

      // Add Final Sales sheet if data exists
      if (data.final_sales && data.final_sales.length > 0) {
        const finalSheet = workbook.addWorksheet("Final Sales");
        finalSheet.mergeCells("A1:F1");
        finalSheet.getCell("A1").value = `Final Sales - ${formatted}`;
        finalSheet.getCell("A1").style = titleStyle;

        finalSheet
          .addRow([
            "State",
            "Taxable Value",
            "IGST Amount",
            "CGST Amount",
            "SGST/UTGST Amount",
            "Invoice Value",
          ])
          .eachCell((cell) => {
            cell.style = headerStyle;
          });

        data.final_sales.forEach((row: SalesData) => {
          finalSheet.addRow([
            row.state_name || "-",
            roundAmount(row.taxable_value),
            roundAmount(row.igst_amount),
            roundAmount(row.cgst_amount),
            roundAmount(row.sgst_amount),
            roundAmount(row.invoice_value),
          ]);
        });

        finalSheet
          .addRow([
            "Grand Total",
            roundAmount(finalTotals.taxable),
            roundAmount(finalTotals.igst),
            roundAmount(finalTotals.cgst),
            roundAmount(finalTotals.sgst),
            roundAmount(finalTotals.invoice),
          ])
          .eachCell((cell) => {
            cell.style = totalStyle;
          });

        finalSheet.columns = [
          { key: "state", width: 20 },
          { key: "taxable", width: 15 },
          { key: "igst", width: 15 },
          { key: "cgst", width: 15 },
          { key: "sgst", width: 15 },
          { key: "invoice", width: 15 },
        ];
      }

      // Check if any sheets were created
      if (workbook.worksheets.length === 0) {
        SmartAlert.show({
          title: "Empty Data",
          content: "All data tables are empty - nothing to export.",
          type: "warning",
        });
        return;
      }

      // Generate and download the file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      SmartAlert.show({
        title: "Export Failed",
        content: "An error occurred while exporting sales data.",
        type: "danger",
      });
    } finally {
      setExporting(false);
    }
  };
  if (isLoading) {
    return <div className="has-text-centered p-5">Loading sales data...</div>;
  }

  if (!data) {
    return <div className="has-text-centered p-5">No data available</div>;
  }

  return (
    <div className="smart-elf-table">
      <div className="has-text-right no-print mb-3" style={{ gap: "0.5rem", display: "flex", justifyContent: "flex-end" }}>
        <button
          className={`button is-small smart-third-button ${exporting ? "is-loading" : ""}`}
          onClick={handlePrint}
          disabled={isLoading || exporting}
        >
          <span className="icon is-small"><i className="fa fa-print"></i></span>
          <span>Print</span>
        </button>
        <button
          className={`button is-small is-success is-light ${exporting ? "is-loading" : ""}`}
          onClick={exportExcel}
          disabled={isLoading || exporting}
        >
          <span className="icon is-small"><i className="fa fa-file-excel-o"></i></span>
          <span>Export Excel</span>
        </button>
      </div>

      <div ref={printRef}>
        {/* Sales Details Table */}
        <div className="sales-table-container mt-4">
          <h1 className="title is-4">Sales Details</h1>
          <div className="table-wrapper is-hidden-mobile is-size-7">
            <table className="table is-bordered is-striped is-hoverable is-fullwidth has-text-centered smart-efl-table">
              <thead className="has-background-warning">
                <tr>
                  <th>State</th>
                  <th>Taxable Value</th>
                  <th>IGST Amount</th>
                  <th>CGST Amount</th>
                  <th>SGST/UTGST Amount</th>
                  <th>Invoice Value</th>
                </tr>
              </thead>
              <tbody>
                {data?.sales_details?.map((row: any, index: any) => (
                  <tr key={`sales-${index}`}>
                    <td>{row?.state_name || "-"}</td>
                    <td>{roundAmount(row?.taxable_value)}</td>
                    <td>{roundAmount(row?.igst_amount)}</td>
                    <td>{roundAmount(row?.cgst_amount)}</td>
                    <td>{roundAmount(row?.sgst_amount)}</td>
                    <td>{roundAmount(row?.invoice_value)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="has-background-warning">
                  <th>Grand Total</th>
                  <th>{roundAmount(totals.taxable)}</th>
                  <th>{roundAmount(totals.igst)}</th>
                  <th>{roundAmount(totals.cgst)}</th>
                  <th>{roundAmount(totals.sgst)}</th>
                  <th>{roundAmount(totals.invoice)}</th>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Credit Notes Table */}
        <div className="sales-table-container mt-4">
          <h1 className="title is-4">Credit Note</h1>
          <div className="table-wrapper is-hidden-mobile is-size-7">
            <table className="table is-bordered is-striped is-hoverable is-fullwidth has-text-centered smart-efl-table">
              <thead className="has-background-warning">
                <tr>
                  <th>State</th>
                  <th>Taxable Value</th>
                  <th>IGST Amount</th>
                  <th>CGST Amount</th>
                  <th>SGST/UTGST Amount</th>
                  <th>Invoice Value</th>
                </tr>
              </thead>
              <tbody>
                {data?.credit_notes?.map((row: any, index: any) => (
                  <tr key={`credit-${index}`}>
                    <td>{row?.state_name || "-"}</td>
                    <td>{roundAmount(row?.taxable_value)}</td>
                    <td>{roundAmount(row?.igst_amount)}</td>
                    <td>{roundAmount(row?.cgst_amount)}</td>
                    <td>{roundAmount(row?.sgst_amount)}</td>
                    <td>{roundAmount(row?.invoice_value)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="has-background-warning">
                  <th>Grand Total</th>
                  <th>{roundAmount(creditTotals.taxable)}</th>
                  <th>{roundAmount(creditTotals.igst)}</th>
                  <th>{roundAmount(creditTotals.cgst)}</th>
                  <th>{roundAmount(creditTotals.sgst)}</th>
                  <th>{roundAmount(creditTotals.invoice)}</th>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Final Sales Table */}
        <div className="sales-table-container mt-3">
          <h1 className="title is-4">Final Sales {formatted}</h1>
          <div className="table-wrapper is-hidden-mobile is-size-7">
            <table className="table is-bordered is-striped is-hoverable is-fullwidth has-text-centered smart-efl-table">
              <thead className="has-background-warning">
                <tr>
                  <th>State</th>
                  <th>Taxable Value</th>
                  <th>IGST Amount</th>
                  <th>CGST Amount</th>
                  <th>SGST/UTGST Amount</th>
                  <th>Invoice Value</th>
                </tr>
              </thead>
              <tbody>
                {data?.final_sales?.map((row: any, index: any) => (
                  <tr key={`final-${index}`}>
                    <td>{row?.state_name || "-"}</td>
                    <td>{roundAmount(row?.taxable_value)}</td>
                    <td>{roundAmount(row?.igst_amount)}</td>
                    <td>{roundAmount(row?.cgst_amount)}</td>
                    <td>{roundAmount(row?.sgst_amount)}</td>
                    <td>{roundAmount(row?.invoice_value)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="has-background-warning">
                  <th>Grand Total</th>
                  <th>{roundAmount(finalTotals.taxable)}</th>
                  <th>{roundAmount(finalTotals.igst)}</th>
                  <th>{roundAmount(finalTotals.cgst)}</th>
                  <th>{roundAmount(finalTotals.sgst)}</th>
                  <th>{roundAmount(finalTotals.invoice)}</th>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesDetailsTable;
